import axios, { AxiosStatusCode } from 'smg/axios';
import * as buttons from 'smg/buttons';
import * as session from 'smg/customer-api/session';
import { HandleRequestError, IsAxiosError, IsBindErrors } from 'smg/errors';
import { ReportValidity } from 'smg/form';
import Modal from 'smg/modal';
import { IsObject } from 'smg/object';
import { Phone } from 'smg/phone';
import * as template from 'smg/template';
import { Success } from 'smg/toast';
import { GetFirstAndLast } from 'smg/users';
import { UUID } from 'smg/uuid';
import { Relref } from './hugo';

export interface LoginCodeOptions {
    phone?: Phone;
    email?: string;
}

export declare interface LoginResp {
    token?: string;
    emails?: string[];
}

interface sessionParams {
    phone?: string;
    email?: string;
    code?: string;
}

interface signUpPrevModal {
    modal: Modal;
    emailOrPhone: 'email' | 'phone' | 'password';
}

export function PhoneLoginModal(prevModal?: Modal): Modal {
    prevModal?.Close();

    const phoneLoginBody = template.Clone('#phone-input-body-template');
    const $loginEmailButton = phoneLoginBody.querySelector('.login-with-email') as HTMLButtonElement;
    const $loginPasswordButton = phoneLoginBody.querySelector('.login-with-password') as HTMLButtonElement;
    const $loginPhoneInputContainer = phoneLoginBody.querySelector('#login-phone-input') as HTMLElement;
    const $loginPhoneInput = $loginPhoneInputContainer.querySelector('input') as HTMLInputElement;
    const p = new Phone($loginPhoneInput);
    const $navTabs = phoneLoginBody.querySelector('#myTab') as HTMLElement;

    const phoneInputModal = new Modal({
        size: 'modal-sm',
        body: phoneLoginBody,
        disableErrScroll: true,
        title: 'Welcome!',
        headerLevel: 'h1',
        appendClassBySelector: {
            '.modal-body': 'px-4 px-sm-8 px-md-12 px-lg-16 px-xl-20 pb-10 pt-0 login-modal-body login-modal-body-phone',
            '.modal-footer': 'd-none',
            '.modal-header': 'pb-0',
            '.modal-title': 'fw-900 pb-8',
        },
        onSubmit: async () => {
            const $confirm = phoneInputModal.Element.querySelector('button[type="submit"]') as HTMLButtonElement;
            buttons.Start($confirm);

            await createLoginCode({ phone: p }).catch(err => {
                if (IsAxiosError(err) && IsBindErrors(err.response?.data) && IsObject(err['response']) && 'phone' in err.response.data) {
                    throw Error('Not a valid phone number.');
                } else {
                    throw Error('Something went wrong! Try again, and if the problem continues, please contact us.');
                }
            });

            void promptLoginCodeModal({ phone: p });
        },
    });

    $navTabs.querySelectorAll('.nav-item').forEach(navItem => {
        navItem.addEventListener('click', () => {
            phoneInputModal.Element.querySelector('.smg-modal-error')?.classList.add('d-none');
            if (navItem.querySelector('#signup-tab')) {
                createSignUpModal({ modal: phoneInputModal, emailOrPhone: 'phone' });
            }
        });
    });

    $loginEmailButton.addEventListener('click', e => {
        e.preventDefault();
        createEmailInputLoginModal(phoneInputModal);
    });

    $loginPasswordButton.addEventListener('click', e => {
        e.preventDefault();
        createPasswordLoginModal(phoneInputModal);
    });

    initGoogleBtn();

    return phoneInputModal;
}

function createEmailInputLoginModal(prevModal?: Modal) {
    prevModal?.Close();

    const loginBody = template.Clone('#email-login-modal-body-template');
    const $loginPasswordButton = loginBody.querySelector('.login-with-password') as HTMLButtonElement;
    const $loginPhoneButton = loginBody.querySelector('.login-with-phone') as HTMLButtonElement;
    const $email = loginBody.querySelector('#login-email') as HTMLInputElement;
    const $navTabs = loginBody.querySelector('#myTab') as HTMLElement;

    const emailLoginModal = new Modal({
        size: 'modal-sm',
        body: loginBody,
        disableErrScroll: true,
        title: 'Welcome!',
        headerLevel: 'h1',
        appendClassBySelector: {
            '.modal-body': 'px-4 px-sm-8 px-md-12 px-lg-16 px-xl-20 pb-10 pt-0 login-modal-body login-modal-body-email',
            '.modal-footer': 'd-none',
            '.modal-header': 'pb-0',
            '.modal-title': 'fw-900 pb-8',
        },
        onSubmit: async () => {
            const $confirm = emailLoginModal.Element.querySelector('button[type="submit"]') as HTMLButtonElement;
            buttons.Start($confirm);

            await createLoginCode({ email: $email.value }).catch(err => {
                if (IsAxiosError(err) && IsBindErrors(err.response?.data) && IsObject(err['response']) && 'phone' in err.response.data) {
                    throw Error('Not a valid email.');
                } else {
                    throw Error('Something went wrong! Try again, and if the problem continues, please contact us.');
                }
            });

            void promptLoginCodeModal({ email: $email.value });
        },
    });

    $navTabs.querySelectorAll('.nav-item').forEach(navItem => {
        navItem.addEventListener('click', () => {
            emailLoginModal.Element.querySelector('.smg-modal-error')?.classList.add('d-none');
            if (navItem.querySelector('#signup-tab')) {
                createSignUpModal({ modal: emailLoginModal, emailOrPhone: 'email' });
            }
        });
    });

    $loginPhoneButton.addEventListener('click', e => {
        e.preventDefault();
        PhoneLoginModal(emailLoginModal);
    });

    $loginPasswordButton.addEventListener('click', e => {
        e.preventDefault();
        createPasswordLoginModal(emailLoginModal);
    });

    initGoogleBtn();
}

function createPasswordLoginModal(prevModal?: Modal) {
    prevModal?.Close();

    const passwordLoginBody = template.Clone('#password-login-modal-body-template');
    const $loginPhoneButton = passwordLoginBody.querySelector('.login-with-phone') as HTMLButtonElement;
    const $loginEmailButton = passwordLoginBody.querySelector('.login-with-email') as HTMLButtonElement;
    const $email = passwordLoginBody.querySelector('#login-email') as HTMLInputElement;
    const $password = passwordLoginBody.querySelector('#login-password') as HTMLInputElement;
    const $forgotPassword = passwordLoginBody.querySelector('#login-forgot-password') as HTMLAnchorElement;
    const $confirm = passwordLoginBody.querySelector('.login-btn') as HTMLButtonElement;
    const $navTabs = passwordLoginBody.querySelector('#myTab') as HTMLElement;

    const passwordLoginModal = new Modal({
        size: 'modal-sm',
        body: passwordLoginBody,
        disableErrScroll: true,
        title: 'Welcome!',
        headerLevel: 'h1',
        appendClassBySelector: {
            '.modal-body': 'px-4 px-sm-8 px-md-12 px-lg-16 px-xl-20 pb-10 pt-0 login-modal-body login-modal-body-password',
            '.modal-footer': 'd-none',
            '.modal-header': 'pb-0',
            '.modal-title': 'fw-900 pb-8',
        },
        onSubmit: () => {
            const $fieldset = passwordLoginModal.Element.querySelector('fieldset') as HTMLFieldSetElement;

            $fieldset.removeAttribute('disabled');
            buttons.Start($confirm);

            return axios.post<LoginResp>('/sessions', {
                'email': $email.value,
                'password': $password.value,
            }).then(resp => {
                if (resp.data.token) {
                    session.SetLoginToken(resp.data.token);
                    window.location.href = Relref('/account');
                    return;
                }

                throw Error('Login failed!');
            }).catch(err => {
                $fieldset.removeAttribute('disabled');
                buttons.Stop($confirm);

                switch (AxiosStatusCode(err)) {
                case 400:
                    throw Error('Invalid email or password');
                default:
                    throw Error('Something went wrong! Try again, and if the problem continues, please contact us.');
                }
            });
        },
    });

    $loginPhoneButton.addEventListener('click', e => {
        e.preventDefault();
        PhoneLoginModal(passwordLoginModal);
    });

    $loginEmailButton.addEventListener('click', e => {
        e.preventDefault();
        createEmailInputLoginModal(passwordLoginModal);
    });

    $forgotPassword.addEventListener('click', e => {
        e.preventDefault();
        ForgotPasswordLoginModal(passwordLoginModal);
    });

    $navTabs.querySelectorAll('.nav-item').forEach(navItem => {
        navItem.addEventListener('click', () => {
            passwordLoginModal.Element.querySelector('.smg-modal-error')?.classList.add('d-none');
            if (navItem.querySelector('#signup-tab')) {
                createSignUpModal({ modal: passwordLoginModal, emailOrPhone: 'password' });
            }
        });
    });

    initGoogleBtn();
}

export function ForgotPasswordLoginModal(prevModal?: Modal) {
    if (prevModal) {
        prevModal.Close();
    } else {
        setTimeout(() => $backBtn.classList.add('d-none'));
    }

    const modalBody = template.Clone('#forgot-password-login-modal-body-template');
    const $email = modalBody.querySelector('#forgot-password-email') as HTMLInputElement;
    const $backBtn = modalBody.querySelector('.forgot-password-back-btn') as HTMLButtonElement;

    const forgotPasswordModal = new Modal({
        size: 'modal-sm',
        body: modalBody,
        disableErrScroll: true,
        title: 'Reset Your Password',
        headerLevel: 'h2',
        appendClassBySelector: {
            '.modal-body': 'px-4 px-sm-8 px-md-12 px-lg-16 px-xl-20 pb-14 pt-0 login-modal-body login-modal-body-forgot-password',
            '.modal-footer': 'd-none',
            '.modal-header': 'px-6 px-sm-8 px-md-12 px-lg-16 px-xl-20 pb-0',
            '.modal-title': 'fw-900 mb-8',
        },
        onSubmit: async () => {
            await axios.post('/password-resets', {
                'email': $email.value,
            }).then(() => {
                forgotPasswordEmailSentModal(forgotPasswordModal);
            }).catch(err => HandleRequestError(err));
        },
    });

    $backBtn.addEventListener('click', e => {
        e.preventDefault();
        createPasswordLoginModal(forgotPasswordModal);
    });
}

function forgotPasswordEmailSentModal(prevModal?: Modal) {
    prevModal?.Close();

    const modalBody = template.Clone('#forgot-password-success-modal-body-template');

    new Modal({
        size: 'modal-sm',
        body: modalBody,
        disableErrScroll: true,
        headerLevel: 'h2',
        appendClassBySelector: {
            '.modal-body': 'px-4 px-sm-8 px-md-12 px-lg-16 px-xl-20 py-6 py-md-12d login-modal-body login-modal-body-forgot-password',
            '.modal-footer': 'd-none',
            '.modal-header': 'd-none',
        },
    });
}

async function promptLoginCodeModal(opts: LoginCodeOptions) {
    const tel = await opts.phone?.Format();

    const body = template.Clone(`#${opts.phone ? 'phone' : 'email'}-code-inputs-template`);
    const phone = body?.querySelector('#modal-phone') as HTMLElement;
    phone.textContent = await opts.phone?.Human() || opts.email || '';
    const code = body?.querySelector('#modal-code') as HTMLInputElement;
    const codeInputs: HTMLInputElement[] = Array.from(code.querySelectorAll('input'));
    const resend = body?.querySelector('#modal-resend') as HTMLAnchorElement;
    const reEnter = body?.querySelector('#re-enter') as HTMLAnchorElement;

    codeInputs.forEach((input, i) => {
        input.addEventListener('input', e => {
            if (e.target instanceof HTMLInputElement) {
                if (e.target.value.length === 1 && i < codeInputs.length - 1) {
                    codeInputs[i + 1].focus();
                }
            }
        });
        input.addEventListener('paste', e => {
            e.preventDefault();
            codeInputs.forEach(input => input.value = '');
            const paste = e.clipboardData?.getData('text');
            if (!paste) {
                return;
            }

            const pasteArr = paste.split('');
            let k = i;
            for (let j = 0; j < codeInputs.length; j++) {
                if (pasteArr[j] && codeInputs[k]) {
                    codeInputs[k].value = pasteArr[j];
                    k++;
                }
            }
        },
        );
    },
    );

    const loginCodeModal = new Modal({
        size: 'modal-sm',
        body,
        disableErrScroll: true,
        title: 'Verification',
        headerLevel: 'h2',
        appendClassBySelector: {
            '.modal-body': 'px-4 px-sm-6 px-md-8 px-xl-20 pb-20 pt-0',
            '.modal-footer': 'd-none',
            '.modal-header': 'pb-0',
            '.modal-title': 'fw-900 pb-8',
        },
        submitAutoFocus: false,
        onSubmit: async () => {
            const codeVal = codeInputs.map(input => input.value).join('');
            const sessionParams: sessionParams = {
                phone: tel,
                email: opts.email,
                code: codeVal,
            };
            await axios.post<LoginResp>('/sessions', {
                'phone': sessionParams.phone,
                'email': sessionParams.email,
                'code': sessionParams.code,
            }).then(resp => {
                if (resp.status === 202 && resp.data.emails?.length) {
                    loginCodeModal.Close();
                    accountSelectionModal(resp.data.emails.sort(), sessionParams);
                    return;
                } else if (resp.data.token) {
                    session.SetLoginToken(resp.data.token);
                    window.location.href = Relref('/account');
                    return;
                }
                throw Error('Login failed!');
            }).catch(err => {
                switch (AxiosStatusCode(err)) {
                case 404:
                    throw Error('Code is invalid or expired.');
                default:
                    throw Error('Something went wrong! Try again, and if the problem continues, please contact us.');
                }
            });
        },
    });

    resend.addEventListener('click', e => {
        e.preventDefault();

        resend.setAttribute('disabled', 'disabled');
        loginCodeModal.HideSuccess();

        void axios.post('/sessions/code', {
            'phone': tel,
            'email': opts.email,
        }).then(() => Success('New Code Sent!')).
            finally(() => {
                resend.removeAttribute('disabled');
            });
    });
    reEnter.addEventListener('click', e => {
        e.preventDefault();
        loginCodeModal.Close();
        if (opts.phone) {
            PhoneLoginModal();
        } else if (opts.email) {
            createEmailInputLoginModal();
        }
    },
    );

    const $goBackBtn = loginCodeModal.Element.querySelector('.go-back-btn') as HTMLElement;
    $goBackBtn.addEventListener('click', e => {
        e.preventDefault();
        loginCodeModal.Close();
        if (opts.phone) {
            PhoneLoginModal();
        } else if (opts.email) {
            createEmailInputLoginModal();
        }
    });
}

function accountSelectionModal(emails: string[], sessionParams: sessionParams) {
    const body = template.Clone('#login-phone-account-selection');
    const $radioContainer = body.querySelector<HTMLElement>('#user-accounts-container');

    for (const email of emails) {
        const uuid = UUID();
        const $container = template.Clone('#login-phone-account-radio').firstElementChild as HTMLElement;
        const $label = $container.querySelector('label') as HTMLLabelElement;
        const $input = $label.querySelector('input') as HTMLInputElement;
        const $email = $label.querySelector('.email-text') as HTMLElement;
        $email.textContent = email;

        $input.id = uuid;
        $input.value = email;
        $label.setAttribute('for', uuid);

        $radioContainer?.appendChild($container);
    }

    const $inputs = Array.from($radioContainer?.querySelectorAll('input') as NodeListOf<HTMLInputElement>);

    const selectAccountModal = new Modal({
        size: 'modal-sm',
        body,
        disableErrScroll: true,
        title: 'Select an Account',
        headerLevel: 'h2',
        appendClassBySelector: {
            '.modal-body': 'px-4 px-sm-6 px-md-8 px-xl-20 pb-14 pt-0',
            '.modal-footer': 'd-none',
            '.modal-header': 'pb-0',
            '.modal-title': 'fw-900 pb-8',
        },
        submitAutoFocus: false,
        onSubmit: () => {
            const email = $inputs.find(input => input.checked)?.value;
            return axios.post<LoginResp>('/sessions', {
                'phone': sessionParams.phone,
                'email': email,
                'code': sessionParams.code,
            }).then(resp => {
                if (resp.data.token) {
                    session.SetLoginToken(resp.data.token);
                    window.location.href = Relref('/account');
                    return;
                }
                throw Error('Login failed!');
            }).catch(err => {
                switch (AxiosStatusCode(err)) {
                case 404:
                    throw new Error('Code is invalid or expired.');
                default:
                    throw Error('Something went wrong! Try again, and if the problem continues, please contact us.');
                }
            });
        },
    });

    $inputs.forEach(input => {
        input.addEventListener('change', () => {
            $inputs.forEach(i => {
                i.setCustomValidity('');
                i.closest('.is-invalid')?.classList.remove('is-invalid');
            });
            const $errorFeedbackDivs = Array.from(selectAccountModal.Element.querySelectorAll('.invalid-feedback'));
            $errorFeedbackDivs.forEach(div => div.remove());
            ReportValidity(selectAccountModal.Element.querySelector('form') as HTMLFormElement);
        });
    });
}

function createSignUpModal(prevModal: signUpPrevModal) {
    prevModal.modal.Close();
    const body = template.Clone('#sign-up-template');
    const $phoneInput = body.querySelector('#sign-up-phone') as HTMLInputElement;
    const phone = new Phone($phoneInput);
    const $name = body.querySelector('#sign-up-name') as HTMLInputElement;
    const $email = body.querySelector('#sign-up-email') as HTMLInputElement;
    const $password = body.querySelector('#sign-up-password') as HTMLInputElement;
    const $passwordConfirm = body.querySelector('#sign-up-password-confirm') as HTMLInputElement;
    const $submitBtn = body.querySelector('button[type="submit"]') as HTMLElement;

    const signUpModal = new Modal({
        size: 'modal-sm',
        body,
        disableErrScroll: true,
        title: 'Welcome!',
        headerLevel: 'h1',
        appendClassBySelector: {
            '.modal-body': 'px-4 px-sm-8 px-md-12 px-lg-16 px-xl-20 pb-10 pt-0',
            '.modal-footer': 'd-none',
            '.modal-header': 'pb-0',
            '.modal-title': 'fw-900 pb-8',
        },
        submitAutoFocus: false,
        preSubmit: () => {
            $password.setCustomValidity('');
            $passwordConfirm.setCustomValidity('');
            if ($password.value !== $passwordConfirm.value) {
                $password.setCustomValidity('Passwords do not match!');
                $passwordConfirm.setCustomValidity('Passwords do not match!');
            }
        },
        onSubmit: async () => {
            buttons.Start($submitBtn);
            const { firstName, lastName } = GetFirstAndLast($name.value);
            await axios.post('/users', {
                'firstName': firstName,
                'lastName': lastName,
                'email': $email.value,
                'phone': await phone.Format(),
                'password': $password.value,
                'confirmPassword': $passwordConfirm.value,
            }).then(async () => {
                buttons.Stop($submitBtn);
                const $alert = signUpModal.Element.querySelector('#signup-alert') as HTMLElement;
                $alert.classList.remove('d-none');
                await promptLoginCode({ email: $email.value }, signUpModal).then(() => window.location.href = Relref('/account')).
                    catch(err => {
                        throw err;
                    });
            }).catch(err => {
                buttons.Stop($submitBtn);
                HandleRequestError(err);
                throw err;
            });
        },
    });

    const $navTabs = signUpModal.Element.querySelector('#myTab') as HTMLElement;
    $navTabs.querySelector('#login-tab')?.classList.remove('active');
    $navTabs.querySelector('#signup-tab')?.classList.add('active');
    $navTabs.querySelectorAll('.nav-item').forEach(navItem => {
        navItem.addEventListener('click', () => {
            if (navItem.querySelector('#login-tab')) {
                signUpModal.Close();
                if (prevModal.emailOrPhone === 'email') {
                    createEmailInputLoginModal();
                } else if (prevModal.emailOrPhone === 'phone') {
                    PhoneLoginModal();
                } else {
                    createPasswordLoginModal();
                }
            }
        });
    });

    initGoogleBtn();
}

async function createLoginCode(opts: LoginCodeOptions) {
    return axios.post('/sessions/code', {
        'phone': await opts.phone?.Format(),
        'email': opts.email,
    });
}

async function promptLoginCode(opts: LoginCodeOptions, prevModal?: Modal) {
    await createLoginCode(opts).then(async () => {
        const body = (document.querySelector('#modal-login-code') as HTMLTemplateElement).content.cloneNode(true) as DocumentFragment;

        const phone = body.querySelector('#modal-phone') as HTMLElement;
        phone.textContent = await opts.phone?.Human() || opts.email || '';

        const code = body.querySelector('#modal-code') as HTMLInputElement;

        const resend = body.querySelector('#modal-resend') as HTMLAnchorElement;
        const resendSpinner = body.querySelector('.spinner-border') as HTMLElement;
        return new Promise<void>(resolve => {
            resend.addEventListener('click', e => {
                e.preventDefault();

                resend.setAttribute('disabled', 'disabled');
                resendSpinner.classList.remove('d-none');
                m.HideSuccess();

                void axios.post('/sessions/code', {
                    'phone': opts.phone,
                    'email': opts.email,
                }).then(() => m.ShowSuccess('Your code has been sent!')).
                    finally(() => {
                        resend.removeAttribute('disabled');
                        resendSpinner.classList.add('d-none');
                    });
            });

            prevModal?.Close();

            const m = new Modal({
                title: 'We just sent you a code',
                headerLevel: 'h2',
                body,
                size: 'modal-sm',
                submitAutoFocus: false,
                onSubmit: async () => axios.post<LoginResp>('/sessions', {
                    'phone': opts.phone,
                    'email': opts.email,
                    'code': code.value,
                }).then(resp => {
                    if (resp.status === 202 && resp.data.emails?.length) {
                        const body = (document.querySelector('#modal-login-select-email') as HTMLTemplateElement).content.cloneNode(true) as DocumentFragment;

                        const listGroup = body.querySelector('.list-group') as HTMLElement;
                        const listGroupItem = listGroup.querySelector('.list-group-item') as HTMLAnchorElement;

                        listGroup.removeChild(listGroupItem);

                        resp.data.emails.sort();
                        for (const email of resp.data.emails) {
                            const a = listGroupItem.cloneNode(true) as HTMLAnchorElement;
                            a.textContent = email;
                            listGroup.appendChild(a);

                            a.addEventListener('click', e => {
                                e.preventDefault();

                                axios.post<LoginResp>('/sessions', {
                                    'phone': opts.phone,
                                    'email': email,
                                    'code': code.value,
                                }).then(resp => {
                                    if (resp.data.token) {
                                        session.SetLoginToken(resp.data.token);
                                        resolve();
                                        return;
                                    }

                                    throw Error('Login failed!');
                                }).catch(err => {
                                    switch (AxiosStatusCode(err)) {
                                    case 404:
                                        throw new Error('Code is invalid or expired.');
                                    default:
                                        throw err;
                                    }
                                });
                            });
                        }

                        new Modal({ body });

                        return;
                    }

                    if (resp.data.token) {
                        session.SetLoginToken(resp.data.token);
                        resolve();
                        return;
                    }

                    throw Error('Login failed!');
                }).catch(err => {
                    switch (AxiosStatusCode(err)) {
                    case 404:
                        throw new Error('Code is invalid or expired.');
                    default:
                        throw err;
                    }
                }),
            });

            // TODO: if modal animation is turned on this doesn't work
            // probably just need to timeout until the animation is done
            setTimeout(() => code.focus());
        });
    });
}

function initGoogleBtn() {
    const googleBtn = document.querySelector('.google-login-button') as HTMLElement;
    googleBtn.addEventListener('click', e => {
        e.preventDefault();
        const hiddenGoogleBtn = document.querySelector('.g_id_signin') as HTMLElement;
        const googleTarget = hiddenGoogleBtn.querySelector('div[tabindex="0"]') as HTMLElement;

        googleTarget.dispatchEvent(new PointerEvent('click'));
    });
}
